/**
 * API-Client für ScanFlow gegen den Hetzner-Webhook.
 *
 * Caddy-Route in Prod: /api/scanflow/* → webhook:8001/scanflow/*
 * Vite-Dev-Proxy in vite.config.ts macht dasselbe lokal.
 *
 * Auth: vimaana_auth nutzt Cookie-Sessions (HttpOnly), credentials: "include"
 * sorgt dafür dass der Browser den Cookie mitschickt.
 */

const BASE = "/api/scanflow";

export type Beleg = {
  id: string;
  source: string;
  source_ref: string;
  lieferant: string;
  lieferant_firma_id: string;
  datum: string;
  netto: number;
  bruttobetrag: number;
  mwst_betrag: number;
  waehrung: string;
  beleg_typ: string;
  notiz: string;
  ki_extrahiert: boolean;
  /** Clara liest den Beleg gerade im Hintergrund */
  ki_laeuft?: boolean;
  /** Meldung, falls Claras letzter Leseversuch scheiterte */
  ki_fehler?: string;
  belegpfad: string;
  projekt_id: string;
  projekt_title: string;
  projekt_ausgabe_id: string;
  status: string;
  ausgabe_typ: string;
  zahlungsweg: string;
  /** Zahlungsart konto|bar (Migration 056). bar = Barkasse, kein Bankabgleich. */
  zahlungsart: string;
  inhaber_id: string;
  /** Privatkauf des Inhabers über die Firma → Privatentnahme (mindert Auszahlung). */
  is_privatentnahme: boolean;
  helfer_id: string;
  is_bezahlt: boolean;
  /** Explizit in den Zahllauf (Überweisungskorb) übernommen. */
  im_zahllauf: boolean;
  /** Fest verknüpfte Bankzeile (leer = noch keiner Buchung zugeordnet). */
  kontobewegung_id: string;
  /** SEPA-Status: none|angewiesen|ausgefuehrt|fehler (Überweisung bleibt Desktop). */
  ueberweisung_status: string;
  /** Am Beleg erkannte Empfänger-IBAN (Grundlage für „IBAN übernehmen"). */
  erkannte_iban: string;
  erkannter_kontoinhaber: string;
  /** true = für den zugeordneten Helfer/Lieferanten ist schon eine IBAN hinterlegt
   *  → „IBAN übernehmen" wird dann nicht angeboten. */
  empfaenger_hat_iban: boolean;
  is_bewirtung: boolean;
  bewirtung_anlass: string;
  bewirtung_teilnehmer_intern: string[];
  bewirtung_teilnehmer_extern: string[];
  bewirtung_trinkgeld: number;
  /** Zeitpunkt der letzten §4-EStG-Annex-Generierung. null = noch kein Bewirtungsbeleg erzeugt → unvollständig. */
  bewirtung_annex_at: string | null;
  an_steuerberater_versendet_at: string | null;
  /** Eigenbeleg-Seite am PDF (bar/privat bezahlt, Migration 230). */
  eigenbeleg_at?: string | null;
  an_steuerberater_email: string;
  created_by_user_id: string;
  app_version: string;
  created_at: string;
  updated_at: string;
  /** Scan-Qualität: leer = ok, sonst menschenlesbare Warnung (Betrag/Datum/Lieferant unsicher). */
  scan_warnung: string;
  scan_konfidenz: { betrag?: number | null; datum?: number | null; lieferant?: number | null };
  /** KI-Kurzbeschreibung des Beleginhalts (was wurde gekauft). */
  ki_beschreibung: string;
};

export type BelegListResponse = {
  total: number;
  items: Beleg[];
};

export type BelegPatch = Partial<{
  lieferant: string;
  lieferant_firma_id: string;
  datum: string;
  netto: number;
  bruttobetrag: number;
  mwst_betrag: number;
  waehrung: string;
  beleg_typ: string;
  notiz: string;
  projekt_id: string;
  status: string;
  ausgabe_typ: string;
  zahlungsweg: string;
  zahlungsart: string;
  inhaber_id: string;
  is_privatentnahme: boolean;
  helfer_id: string;
  is_bezahlt: boolean;
  im_zahllauf: boolean;
  is_bewirtung: boolean;
  bewirtung_anlass: string;
  bewirtung_teilnehmer_intern: string[];
  bewirtung_teilnehmer_extern: string[];
  bewirtung_trinkgeld: number;
}>;

export type ProjektShort = {
  id: string;
  title: string;
  status: string;
  kunde_name: string;
  ort: string;
  /** nur bei today=true: man ist heute selbst auf dieser Baustelle eingeteilt */
  mein?: boolean;
};

/** Abgeschlossen oder archiviert — wird zur Zuordnung angeboten (Belege kommen oft
 *  erst nach dem Ende der Baustelle), aber als solches gekennzeichnet. Der Status
 *  trägt ein Emoji-Präfix („⚫ abgeschlossen"), deshalb Teilwort-Vergleich. */
export function istAbgeschlossen(p: Pick<ProjektShort, "status">): boolean {
  const s = (p.status || "").toLowerCase();
  return s.includes("abgeschlossen") || s.includes("archiviert");
}

export type Inhaber = {
  id: string;
  name: string;
  /** true für den angemeldeten Nutzer — Vorbelegung bei Einlage/Entnahme. */
  ich: boolean;
};

export type TeamMember = {
  id: string;
  name: string;
  role: "inhaber" | "helfer";
  active: boolean;
};

export type Lieferant = {
  id: string;
  name: string;
};

/** Eine Position eines Lieferscheins (Material + Menge). */
export type LieferscheinPosition = {
  id: string;
  beleg_id: string;
  position_nr: number;
  katalog_id: string;
  katalog_name: string;
  freitext_name: string;
  menge: number;
  einheit: string;
  projekt_id: string;
  projekt_title: string;
  abgerechnet_menge: number;
  ki_extrahiert: boolean;
  ki_konfidenz: number;
  notiz: string;
  /** Woher die Material-Zuordnung stammt: "" / "vorschlag" = noch offen,
   *  "gedaechtnis" / "katalog" / "mensch" = sicher, "ignoriert" = keine Ware
   *  (Fracht, Pfand, Maut). Sichere Zuordnungen lernt der Lieferant mit. */
  zuordnung?: string;
};

export type LieferscheinPositionPatch = Partial<{
  katalog_id: string;
  freitext_name: string;
  menge: number;
  einheit: string;
  projekt_id: string;
  notiz: string;
}>;

export type MaterialKatalog = {
  id: string;
  name: string;
  einheit: string;
  kategorie: string;
};

export type Scanner = {
  id: string;
  name: string;
  anzahl: number;
};

export type ScannerOptions = {
  /** Aktuell eingeloggter User (id leer falls Session ohne user_id). */
  me: { id: string; name: string };
  /** Alle User, die schon ScanFlow-Belege erfasst haben. */
  scanner: Scanner[];
};

/** Eine Bankbuchung mit gleichem Betrag wie der Beleg — Kandidat zum Zuordnen. */
export type MatchingBuchung = {
  id: string;
  buchungsdatum: string;
  betrag: number;
  verwendungszweck: string;
  gegenpartei_name: string;
  /** true = an dieser Bankzeile hängt schon ein Beleg. */
  hat_beleg: boolean;
};

/** Von der Bank gemeldeter Kontostand je IBAN (read-only, aus konto_saldo). */
export type KontoStatus = {
  iban: string;
  saldo: number;
  waehrung: string;
  stand_am: string;
  bank_label: string;
  updated_at: string;
};

/** Eine versandbereite Rechnung für den DATEV-Batch. */
export type DatevPending = {
  beleg_id: string;
  lieferant: string;
  datum: string;
  betrag: number;
  waehrung: string;
  is_pdf: boolean;
};

export type DatevSendResult = {
  gesendet: number;
  uebersprungen: number;
  fehler: { beleg_id: string; lieferant: string; grund: string }[];
  to_email: string;
};

/** Ein offener Posten im Zahllauf (Überweisungskorb). */
export type ZahllaufPosten = {
  beleg_id: string;
  lieferant: string;
  empfaenger: string;
  datum: string;
  betrag: number;
  ausgabe_typ: string;
  /** nur bei fast_zahlbereit: am Beleg erkannte IBAN, die noch übernommen werden muss */
  erkannte_iban: string;
  /** nur bei klaerfall: was fehlt */
  grund: string;
};

export type Zahllauf = {
  zahlbereit: ZahllaufPosten[];
  fast_zahlbereit: ZahllaufPosten[];
  klaerfall: ZahllaufPosten[];
  summe_zahlbereit: number;
  summe_gesamt: number;
};

/** Einheitliches Helfer-Konto: verdient (Stunden+Pauschale) vs. belegt (Rechnungen) vs. ausgezahlt. */
export type HelferKonto = {
  member_id: string;
  name: string;
  role: string;
  active: boolean;
  verdient: number;
  belegt: number;
  ausgezahlt: number;
  offen: number;          // verdient - ausgezahlt (Guthaben)
  rechnung_fehlt: number; // ausgezahlt - belegt
  unberechnet: number;    // verdient - belegt
  letzte_auszahlung: string;
};

export type Duplicate = {
  id: string;
  lieferant: string;
  datum: string;
  bruttobetrag: number;
  status: string;
  grund: "hash" | "felder" | string;
};

/**
 * Ist der Beleg noch unfertig — also weder gebucht noch abgeschlossen?
 *
 * Es gibt ZWEI solche Stati, nicht einen. `entwurf` entsteht beim Scannen,
 * `eingereicht` beim Hochladen aus der Team-App (Helferrechnungen). Das Backend
 * behandelt beide überall gleich (`status IN ('entwurf','eingereicht')`);
 * ScanFlow prüfte lange nur auf `entwurf`. Folge: eine eingereichte
 * Helferrechnung tauchte in der Entwürfe-Liste gar nicht auf, und das
 * Abschließen sprang still über `/finalize` hinweg — der Beleg blieb für immer
 * unfertig stehen, während Kontor ihn korrekt als Entwurf führte
 * (Benjamin, 19.08.2026). Eine Regel, ein Ort.
 */
export function istEntwurfStatus(status: string | undefined | null): boolean {
  return status === "entwurf" || status === "eingereicht";
}

export const BELEG_STATI = [
  "entwurf",
  "eingereicht",
  "erfasst",
  "an_steuerberater_gesendet",
  "verbucht",
  "abgelegt",
] as const;
// Hinweis: Status wird in ScanFlow nur angezeigt, nicht editiert — der
// DATEV-Versand und die Status-Pflege passieren in der Desktop-App.

export const BELEG_TYPEN = [
  "",
  "kassenbeleg",
  "rechnung",
  "bewirtungsbeleg",
  "lieferschein",
  "sonstiges",
] as const;

export const AUSGABE_TYPEN = [
  "lieferant",
  "helfer",
  "betrieb",
  "verpflegung",
] as const;
export type AusgabeTyp = (typeof AUSGABE_TYPEN)[number];

// ──────────────────────────────────────────────────────────────

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
    ...init,
  });
  if (!r.ok) {
    let detail = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

async function uploadBeleg(
  pages: Blob[],
  fields: {
    lieferant?: string;
    datum?: string;
    netto?: number;
    waehrung?: string;
    beleg_typ?: string;
    notiz?: string;
    projekt_id?: string;
    /** Lieferscheine: JSON-Array von projekt.id (Mehrfach-Vorauswahl). */
    projekt_ids?: string;
    app_version?: string;
    ausgabe_typ?: string;
    helfer_id?: string;
    is_bewirtung?: boolean;
    /** JSON-String mit Capture-Telemetrie (Auflösung, Schärfe, Detection). */
    capture_meta?: string;
    /** "beleg" | "positionen": Clara liest nach dem Hochladen im Hintergrund */
    auswerten?: string;
  },
): Promise<Beleg> {
  const fd = new FormData();
  pages.forEach((b, i) => fd.append("pages", b, `seite-${i + 1}.jpg`));
  for (const [k, v] of Object.entries(fields)) {
    if (v === undefined || v === null || v === "") continue;
    fd.append(k, typeof v === "boolean" ? (v ? "true" : "false") : String(v));
  }
  const r = await fetch(`${BASE}/belege`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!r.ok) {
    let detail = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return r.json() as Promise<Beleg>;
}

// ── Private Dokumente (F1) ────────────────────────────────────────────────
export type PrivatDok = {
  id: string;
  titel: string;
  tags: string[];
  notiz: string;
  belegpfad: string;
  ki_beschreibung: string;
  dokumentdatum: string;
  haendler: string;
  preis: number;
  waehrung: string;
  garantie_monate: number | null;
  seriennummer: string;
  garantie_ende: string;
  /** "" | aktiv | bald | abgelaufen */
  garantie_status: string;
  /** true = Tag "Garantie" gesetzt → Garantie-Block sichtbar. */
  hat_garantie: boolean;
  created_at: string;
  updated_at: string;
  /** Clara liest das Dokument gerade im Hintergrund. */
  ki_laeuft?: boolean;
  /** Letzte Meldung, falls Clara nicht lesen konnte. */
  ki_fehler?: string;
};

export type PrivatDokPatch = Partial<{
  titel: string;
  tags: string[];
  notiz: string;
  dokumentdatum: string;
  haendler: string;
  preis: number;
  waehrung: string;
  garantie_monate: number | null;
  seriennummer: string;
}>;

async function uploadPrivat(
  pages: Blob[],
  fields: {
    titel?: string;
    tags?: string[];
    notiz?: string;
    dokumentdatum?: string;
    app_version?: string;
    /** "privat" = Clara liest danach im Hintergrund */
    auswerten?: "privat";
  },
): Promise<PrivatDok> {
  const fd = new FormData();
  pages.forEach((b, i) => fd.append("pages", b, `seite-${i + 1}.jpg`));
  if (fields.titel) fd.append("titel", fields.titel);
  if (fields.tags && fields.tags.length) fd.append("tags", JSON.stringify(fields.tags));
  if (fields.notiz) fd.append("notiz", fields.notiz);
  if (fields.dokumentdatum) fd.append("dokumentdatum", fields.dokumentdatum);
  if (fields.app_version) fd.append("app_version", fields.app_version);
  if (fields.auswerten) fd.append("auswerten", fields.auswerten);
  const r = await fetch(`${BASE}/privat`, { method: "POST", credentials: "include", body: fd });
  if (!r.ok) {
    let detail = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return r.json() as Promise<PrivatDok>;
}

export const api = {
  uploadBeleg,
  uploadPrivat,

  /** Clara liest den Beleg im Hintergrund — Antwort sofort, `ki_laeuft` = true. */
  lesenLassen(id: string): Promise<Beleg> {
    return request<Beleg>(`/belege/${encodeURIComponent(id)}/lesen`, { method: "POST" });
  },

  extractBeleg(id: string): Promise<Beleg> {
    return request<Beleg>(`/belege/${encodeURIComponent(id)}/extract`, {
      method: "POST",
    });
  },

  /** Entwurf abschließen → bucht + benennt + Status 'erfasst'. */
  finalizeBeleg(id: string): Promise<Beleg> {
    return request<Beleg>(`/belege/${encodeURIComponent(id)}/finalize`, {
      method: "POST",
    });
  },

  /** Mögliche Dubletten zu diesem Beleg (Warnung beim Verarbeiten). */
  findDuplicates(id: string): Promise<Duplicate[]> {
    return request<Duplicate[]>(`/belege/${encodeURIComponent(id)}/duplicates`);
  },

  listBelege(
    params: {
      status?: string;
      source?: string;
      projekt_id?: string;
      created_by_user_id?: string;
      limit?: number;
      offset?: number;
      /** Suche über alle Belege (Server), jedes Wort muss vorkommen. */
      q?: string;
    } = {},
  ): Promise<BelegListResponse> {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.source) q.set("source", params.source);
    if (params.projekt_id) q.set("projekt_id", params.projekt_id);
    if (params.created_by_user_id) q.set("created_by_user_id", params.created_by_user_id);
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    if (params.q) q.set("q", params.q);
    const qs = q.toString();
    return request<BelegListResponse>(`/belege${qs ? `?${qs}` : ""}`);
  },

  /** Scanner-Optionen für den Inbox-Filter (aktueller User + alle Scanner). */
  listScanner(): Promise<ScannerOptions> {
    return request<ScannerOptions>("/scanner");
  },

  getBeleg(id: string): Promise<Beleg> {
    return request<Beleg>(`/belege/${encodeURIComponent(id)}`);
  },

  patchBeleg(id: string, patch: BelegPatch): Promise<Beleg> {
    return request<Beleg>(`/belege/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  /**
   * Beleg vollständig löschen: projekt_ausgabe + Nextcloud-PDF + DB-Zeile.
   * 409 wenn schon an DATEV versendet. 204 bei Erfolg.
   */
  /**
   * Fehlerfassung aus dem Verkehr ziehen (z. B. Dublette): vor DATEV echtes Löschen,
   * danach Storno — bleibt durchgestrichen stehen, Buchungen fallen weg.
   */
  verwerfenBeleg(id: string, grund: string): Promise<{ modus: "geloescht" | "storniert"; hinweis: string }> {
    return request(`/belege/${encodeURIComponent(id)}/verwerfen`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ grund }),
    });
  },

  deleteBeleg(id: string): Promise<void> {
    return request<void>(`/belege/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  listProjekte(opts: { today?: boolean; includeAll?: boolean } = {}): Promise<ProjektShort[]> {
    const q = new URLSearchParams();
    if (opts.today) q.set("today", "true");
    if (opts.includeAll) q.set("include_all", "true");
    const qs = q.toString();
    return request<ProjektShort[]>(`/projekte${qs ? `?${qs}` : ""}`);
  },

  /** Aktive Inhaber für die Zahlweg-Auswahl; `ich` = der angemeldete Nutzer. */
  listInhaber(): Promise<Inhaber[]> {
    return request<Inhaber[]>("/inhaber");
  },
  listTeam(): Promise<TeamMember[]> {
    return request<TeamMember[]>("/team");
  },

  /** Kanonische Lieferantenliste für Erkennung + Picker. */
  listLieferanten(): Promise<Lieferant[]> {
    return request<Lieferant[]>("/lieferanten");
  },

  /** Neuen Lieferanten anlegen (nur Name Pflicht; Dedup serverseitig).
   *  force=true bei explizitem "neu anlegen"-Klick: überspringt die Fuzzy-Dedup,
   *  damit ein bewusst neuer Lieferant nicht auf einen falsch erkannten kollabiert. */
  createLieferant(name: string, force = false): Promise<Lieferant> {
    return request<Lieferant>("/lieferanten", {
      method: "POST",
      body: JSON.stringify({ name, force }),
    });
  },

  // ── Lieferscheine ──────────────────────────────────────────────────────
  /** KI-Positions-Extraktion für einen Lieferschein (Lieferant + Material/Menge). */
  extractPositionen(id: string): Promise<LieferscheinPosition[]> {
    return request<LieferscheinPosition[]>(
      `/belege/${encodeURIComponent(id)}/extract-positionen`,
      { method: "POST" },
    );
  },

  /** Positionen eines Lieferscheins lesen. */
  listPositionen(id: string): Promise<LieferscheinPosition[]> {
    return request<LieferscheinPosition[]>(`/belege/${encodeURIComponent(id)}/positionen`);
  },

  /** Vorausgewählte Projekte eines Lieferscheins (für den Positions-Picker). */
  listLieferscheinProjekte(id: string): Promise<{ projekt_id: string; projekt_title: string }[]> {
    return request<{ projekt_id: string; projekt_title: string }[]>(
      `/belege/${encodeURIComponent(id)}/projekte`,
    );
  },

  /** Eine Position korrigieren (Menge/Material/Einheit/Projekt/Notiz). */
  patchPosition(positionId: string, patch: LieferscheinPositionPatch): Promise<LieferscheinPosition> {
    return request<LieferscheinPosition>(`/positionen/${encodeURIComponent(positionId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  /** Eine Position von Hand hinzufügen — wenn die Erkennung etwas übersehen hat. */
  addPosition(
    belegId: string,
    body: { katalog_id?: string; freitext_name?: string; menge?: number; einheit?: string },
  ): Promise<LieferscheinPosition> {
    return request<LieferscheinPosition>(`/belege/${encodeURIComponent(belegId)}/positionen`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** Eine falsch erkannte Position entfernen (409, wenn schon abgerechnet). */
  deletePosition(positionId: string): Promise<{ geloescht: string; beleg_id: string }> {
    return request<{ geloescht: string; beleg_id: string }>(`/positionen/${encodeURIComponent(positionId)}`, {
      method: "DELETE",
    });
  },

  /** Kanonischer Material-Katalog für den Positions-Picker. */
  listMaterialKatalog(): Promise<MaterialKatalog[]> {
    return request<MaterialKatalog[]>("/material-katalog");
  },

  /** Neues Material aus dem Lieferschein-Flow anlegen (Dedup serverseitig). */
  createMaterial(name: string, einheit = "", kategorie = ""): Promise<MaterialKatalog> {
    return request<MaterialKatalog>("/material-katalog", {
      method: "POST",
      body: JSON.stringify({ name, einheit, kategorie }),
    });
  },

  /** Member-IDs (Helfer+Inhaber), die dem Projekt an diesem Datum zugewiesen sind. */
  projektPersonen(projektId: string, datum: string): Promise<string[]> {
    return request<string[]>(
      `/projekte/${encodeURIComponent(projektId)}/personen?datum=${encodeURIComponent(datum)}`,
    );
  },

  // ── Konto-Zuordnung mobil (Belegverarbeitung unterwegs) ────────────────
  /** Bankbuchungen mit gleichem Betrag wie der Beleg — Kandidaten zum Zuordnen. */
  matchingBuchungen(id: string, limit = 10): Promise<MatchingBuchung[]> {
    return request<MatchingBuchung[]>(
      `/belege/${encodeURIComponent(id)}/matching-buchungen?limit=${limit}`,
    );
  },

  /** Beleg fest mit einer Bankzeile verknüpfen (setzt status='verbucht'). */
  matchBuchung(id: string, kontobewegungId: string): Promise<Beleg> {
    return request<Beleg>(`/belege/${encodeURIComponent(id)}/match`, {
      method: "POST",
      body: JSON.stringify({ kontobewegung_id: kontobewegungId }),
    });
  },

  /** Beleg↔Bankzeilen-Verknüpfung wieder lösen. */
  unmatchBuchung(id: string): Promise<Beleg> {
    return request<Beleg>(`/belege/${encodeURIComponent(id)}/unmatch`, {
      method: "POST",
    });
  },

  /** Erkannte IBAN in die Stammdaten des zugeordneten Lieferanten/Helfers übernehmen. */
  uebernehmeIban(
    id: string,
    body: { iban?: string; bic?: string; kontoinhaber?: string } = {},
  ): Promise<{ ok: boolean; target: string; iban: string; kontoinhaber: string }> {
    return request(`/belege/${encodeURIComponent(id)}/iban-uebernehmen`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** Letzter von der Bank gemeldeter Kontostand + Stand-Datum (Pull bleibt Desktop). */
  kontoStatus(): Promise<KontoStatus[]> {
    return request<KontoStatus[]>("/konto-status");
  },

  /** Zahllauf: offene, zu überweisende Rechnungen (klassifiziert). Ausführung am Desktop. */
  zahllauf(): Promise<Zahllauf> {
    return request<Zahllauf>("/zahllauf");
  },

  /** Einheitliches Helfer-Konto (verdient/belegt/ausgezahlt + Divergenzen). */
  helferKonto(): Promise<HelferKonto[]> {
    return request<HelferKonto[]>("/helfer-konto");
  },

  // ── DATEV-Batch ────────────────────────────────────────────────────────
  /** Alle versandbereiten Rechnungen (kein Entwurf, Betrag, Datei, noch nicht gesendet). */
  datevPending(): Promise<DatevPending[]> {
    return request<DatevPending[]>("/datev/pending");
  },

  /** Versandbereite Rechnungen an DATEV senden. Ohne IDs = alle. */
  datevSendBatch(belegIds?: string[]): Promise<DatevSendResult> {
    return request<DatevSendResult>("/datev/send-batch", {
      method: "POST",
      body: JSON.stringify({ beleg_ids: belegIds ?? null }),
    });
  },

  /** PDF-URL für Vollbild-Vorschau (Browser nutzt sie als src eines <object>/<iframe>) */
  pdfUrl(id: string): string {
    return `${BASE}/belege/${encodeURIComponent(id)}/pdf`;
  },

  thumbnailUrl(id: string, w = 120, page?: number): string {
    const pg = page !== undefined ? `&page=${page}` : "";
    return `${BASE}/belege/${encodeURIComponent(id)}/thumbnail?w=${w}${pg}`;
  },

  // ── Private Dokumente (F1) ───────────────────────────────────────────────
  listPrivat(params: { q?: string; tag?: string; expiring?: boolean } = {}): Promise<PrivatDok[]> {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.tag) qs.set("tag", params.tag);
    if (params.expiring) qs.set("expiring", "true");
    const s = qs.toString();
    return request<PrivatDok[]>(`/privat${s ? `?${s}` : ""}`);
  },
  getPrivat(id: string): Promise<PrivatDok> {
    return request<PrivatDok>(`/privat/${encodeURIComponent(id)}`);
  },
  /** KI-Extraktion: füllt leere Felder (Titel/Händler/Datum/Preis) + schlägt Garantie vor. */
  /** Clara liest im Hintergrund — Antwort sofort mit ki_laeuft=true. */
  privatLesenLassen(id: string): Promise<PrivatDok> {
    return request<PrivatDok>(`/privat/${encodeURIComponent(id)}/lesen`, { method: "POST" });
  },
  extractPrivat(id: string): Promise<PrivatDok> {
    return request<PrivatDok>(`/privat/${encodeURIComponent(id)}/extract`, { method: "POST" });
  },
  /** Bereits verwendete Tags des Nutzers — als Vorschläge. */
  listPrivatTags(): Promise<string[]> {
    return request<string[]>("/privat-tags");
  },
  patchPrivat(id: string, patch: PrivatDokPatch): Promise<PrivatDok> {
    return request<PrivatDok>(`/privat/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  deletePrivat(id: string): Promise<{ ok: boolean; id: string }> {
    return request<{ ok: boolean; id: string }>(`/privat/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  privatPdfUrl(id: string): string {
    return `${BASE}/privat/${encodeURIComponent(id)}/pdf`;
  },
  /** Seitenzahl — Grundlage des Blätterns im Betrachter. */
  privatSeiten(id: string): Promise<{ seiten: number }> {
    return request<{ seiten: number }>(`/privat/${encodeURIComponent(id)}/seiten`);
  },
  /** Eine Seite als Bild. `page` ist nullbasiert. */
  privatThumbUrl(id: string, w = 300, page = 0): string {
    return `${BASE}/privat/${encodeURIComponent(id)}/thumbnail?w=${w}&page=${page}`;
  },
};
